import React from 'react';
import styles from './style.module.scss';
import ListingCardCarousel from 'components/listing-card-carousel';
import Listing, { ListingSerializableProperties } from 'data/listing/client-app/types/listing';
import { useOnListingCardClick } from 'utils/listing-card';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import type { ClientPreferences } from 'data/client-info-pages';

export interface RecommendedListingsProps {
  /** The listings to display (serialized from server) */
  listings: ListingSerializableProperties[];
  /** Client name for personalization */
  clientName: string;
  /** Client preferences to show what filters were applied */
  preferences?: ClientPreferences;
}

/**
 * Displays a carousel of recommended listings based on client preferences.
 * Used on custom (agent-created) info pages.
 */
const RecommendedListings = ({ listings, clientName, preferences }: RecommendedListingsProps) => {
  const onListingCardClick = useOnListingCardClick();
  const { t } = useTranslation(Namespaces.COMMON);

  if (!listings || listings.length === 0) {
    return null;
  }

  // Hydrate listings from serialized props and filter valid ones
  const listingCardData = listings
    .map(data => {
      const listing = Listing.fromJSON(data);
      // displayAddress may be null from serialization; treat null as true
      // since these are recommended listings the agent wants to display
      if (listing.displayAddress === null) {
        listing.displayAddress = true;
      }
      return listing;
    })
    .filter(listing => listing && listing.id && listing.isImageReady && (listing.price === null || listing.price >= 10000));

  if (listingCardData.length === 0) {
    return null;
  }

  // Build a description of applied filters
  const filterDescriptions: string[] = [];
  if (preferences) {
    if (preferences.budget_min || preferences.budget_max) {
      const min = preferences.budget_min ? `$${Number(preferences.budget_min).toLocaleString()}` : '';
      const max = preferences.budget_max ? `$${Number(preferences.budget_max).toLocaleString()}` : '';
      if (min && max) {
        filterDescriptions.push(t(LOCALE_COMMON_PATHS.CITYINFO_RECOMMENDEDLISTINGS_PRICERANGE, { min, max, defaultValue: '{{min}} - {{max}}' }));
      } else if (min) {
        filterDescriptions.push(t(LOCALE_COMMON_PATHS.CITYINFO_RECOMMENDEDLISTINGS_PRICEMINPLUS, { min, defaultValue: '{{min}}+' }));
      } else if (max) {
        filterDescriptions.push(t(LOCALE_COMMON_PATHS.CITYINFO_RECOMMENDEDLISTINGS_PRICEUPTO, { max, defaultValue: 'Up to {{max}}' }));
      }
    }
    if (preferences.min_bedrooms) {
      filterDescriptions.push(t(LOCALE_COMMON_PATHS.CITYINFO_RECOMMENDEDLISTINGS_BEDSPLUS, { count: preferences.min_bedrooms, defaultValue: '{{count}}+ beds' }));
    }
    if (preferences.min_bathrooms) {
      filterDescriptions.push(t(LOCALE_COMMON_PATHS.CITYINFO_RECOMMENDEDLISTINGS_BATHSPLUS, { count: preferences.min_bathrooms, defaultValue: '{{count}}+ baths' }));
    }
    if (preferences.property_types && preferences.property_types.length > 0) {
      const types = preferences.property_types.map(t => 
        t.charAt(0).toUpperCase() + t.slice(1)
      );
      if (types.length <= 2) {
        filterDescriptions.push(types.join(', '));
      }
    }
  }

  const firstName = clientName.split(' ')[0].replace(/\b\w/g, c => c.toUpperCase());

  return (
    <section className={styles.component}>
      <div className={styles.header}>
        <h2 className={styles.heading}>
          {t(LOCALE_COMMON_PATHS.CITYINFO_RECOMMENDEDLISTINGS_HEADING, { name: firstName, defaultValue: 'Recommended Homes for {{name}}' })}
        </h2>
        {filterDescriptions.length > 0 && (
          <p className={styles.filters}>
            {t(LOCALE_COMMON_PATHS.CITYINFO_RECOMMENDEDLISTINGS_BASEDON, { filters: filterDescriptions.join(' • '), defaultValue: 'Based on: {{filters}}' })}
          </p>
        )}
      </div>
      <div className={styles.carouselContainer}>
        <ListingCardCarousel
          listingCardsData={{
            listings: listingCardData,
            userLocation: null,
          }}
          onListingCardClick={onListingCardClick}
        />
      </div>
    </section>
  );
};

export default RecommendedListings;
